import type { ReactNode } from 'react';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button, StepIndicator } from '~/components/ui';

export type WizardStep<StepId extends string> = {
  id: StepId;
  label: string;
  title: string;
  description: string;
};

export type WizardPrimaryAction = {
  label: string;
  testId: string;
  disabled: boolean;
  loading?: boolean;
  onClick: () => void;
};

type WizardStepFooterProps<StepId extends string> = {
  onStepChange: (stepId: StepId) => void;
  backLabel: string;
  primaryAction: WizardPrimaryAction;
  footer?: never;
};

type WizardCustomFooterProps = {
  footer: ReactNode;
  onStepChange?: never;
  backLabel?: never;
  primaryAction?: never;
};

type WizardModalProps<StepId extends string> = {
  onClose: () => void;
  isPending: boolean;
  steps: WizardStep<StepId>[];
  currentStepId: StepId;
  testIdPrefix: string;
  testId?: string;
  stepIndicatorTestId?: string;
  stepIndicatorAriaLabel?: string;
  children: ReactNode;
} & (WizardStepFooterProps<StepId> | WizardCustomFooterProps);

export function WizardModal<StepId extends string>(
  props: Readonly<WizardModalProps<StepId>>
) {
  const {
    onClose,
    isPending,
    steps,
    currentStepId,
    testIdPrefix,
    testId,
    stepIndicatorTestId = `${testIdPrefix}-step-indicator`,
    stepIndicatorAriaLabel,
    children,
  } = props;
  const currentStepIndex = steps.findIndex((step) => step.id === currentStepId);
  const currentStep = steps[currentStepIndex];

  return (
    <Modal isOpen onClose={onClose} canClose={!isPending}>
      <div
        className="flex h-[min(760px,92vh)] w-[min(1024px,94vw)] flex-col overflow-hidden rounded-2xl bg-neut-light shadow-2xl"
        data-testid={testId}
      >
        <ModalHeader
          title={currentStep.title}
          description={currentStep.description}
          onClose={onClose}
          closeDisabled={isPending}
        />

        <div className="shrink-0 border-y border-neut-border bg-neut-50 px-8 py-4">
          <StepIndicator
            steps={steps}
            currentStepId={currentStepId}
            testId={stepIndicatorTestId}
            ariaLabel={stepIndicatorAriaLabel}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-8 py-5">
          {children}
        </div>

        {props.primaryAction === undefined ? (
          props.footer
        ) : (
          <WizardStepFooter
            previousStep={
              currentStepIndex > 0 ? steps[currentStepIndex - 1] : null
            }
            isPending={isPending}
            testIdPrefix={testIdPrefix}
            onStepChange={props.onStepChange}
            backLabel={props.backLabel}
            primaryAction={props.primaryAction}
          />
        )}
      </div>
    </Modal>
  );
}

function WizardStepFooter<StepId extends string>({
  previousStep,
  isPending,
  testIdPrefix,
  onStepChange,
  backLabel,
  primaryAction,
}: Readonly<
  Omit<WizardStepFooterProps<StepId>, 'footer'> & {
    previousStep: WizardStep<StepId> | null;
    isPending: boolean;
    testIdPrefix: string;
  }
>) {
  return (
    <WizardFooter
      start={
        previousStep !== null && (
          <Button
            variant="secondary"
            width="auto"
            data-testid={`${testIdPrefix}-back`}
            disabled={isPending}
            onClick={() => onStepChange(previousStep.id)}
          >
            {backLabel}
          </Button>
        )
      }
      end={
        <Button
          variant="primary"
          width="auto"
          data-testid={primaryAction.testId}
          loading={primaryAction.loading}
          disabled={primaryAction.disabled}
          onClick={primaryAction.onClick}
        >
          {primaryAction.label}
        </Button>
      }
    />
  );
}

export function WizardFooter({
  start,
  end,
}: Readonly<{ start?: ReactNode; end: ReactNode }>) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-t border-neut-border bg-neut-50 px-8 py-4">
      <div>{start}</div>
      <div className="flex items-center gap-3">{end}</div>
    </div>
  );
}
